/**
 * The cards the D-2 stack renders — one shell, seven bodies.
 *
 * The bodies are their v1 layouts, unchanged: recruiters have muscle memory for
 * where "Call now" and "Ignore" sit, and the streamline is about where cards
 * appear and how many, not about redrawing them. The alert body is driven
 * entirely by the KINDS record imported from `alertKinds`, so all eight kinds
 * keep their band, ring, icon, title, badge, detail, countdown wording and
 * action label from one table.
 *
 * The shell exists so every card in the stack shares one radius, shadow, dark
 * treatment and pointer-events posture (the stack container is
 * pointer-events-none so its transparent padding never swallows a click meant
 * for the page beneath).
 */
import { FormEvent, ReactNode, useEffect, useState } from 'react';
import {
  AtSign, Check, ChevronDown, ChevronUp, ClipboardCheck, ListChecks, Loader2, MessageSquare, Phone, Reply, Star, X,
} from 'lucide-react';
import api from '../../../services/api';
import CoverageBadge from '../CoverageBadge';
import { defFor, pad, waitingFor } from './alertKinds';
import type { CrmAlert } from './alertKinds';
import type { LastCall } from '../../contexts/SoftphoneContext';
import { timeLabel } from './useCallbacksFeed';
import type { CallbackCard } from './useCallbacksFeed';
import type { SmsCard } from './useSmsFeed';
import { resultLabel } from './useReviewsFeed';
import type { ReviewReadyCard } from './useReviewsFeed';
import type { OutreachAssignedCard } from './useOutreachFeed';
import type { MentionCard } from '../../mentions/mentionLogic';

const CARD_BASE = 'pointer-events-auto overflow-hidden rounded-xl bg-white dark:bg-gray-800';
/** Shadow + border live per-variant: two competing shadow utilities in one
 *  class string resolve by stylesheet order, not by the order you wrote them. */
const CARD_VARIANT = {
  toast: 'border border-gray-200 shadow-lg dark:border-gray-700',
  alert: 'border ring-1 shadow-xl',
} as const;
const CARD_INTERACTIVE =
  'cursor-pointer text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700/60';

/** The dismiss "x" every toast carries, at a 44px target down to sm. */
const CLOSE_BTN =
  'inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded text-gray-400 transition-colors duration-150 hover:text-gray-600 dark:hover:text-gray-300 sm:min-h-0 sm:min-w-0 sm:p-0.5';

export interface OverlayCardProps {
  children: ReactNode;
  variant?: keyof typeof CARD_VARIANT;
  /** Border/ring colors for the alert variant (from the kind table). */
  toneCls?: string;
  /** Whole-card activation. Enter only, and only on the card itself — the
   *  buttons inside it stop propagation and handle their own keys. */
  onActivate?: () => void;
  ariaLabel?: string;
}

export function OverlayCard({ children, variant = 'toast', toneCls = '', onActivate, ariaLabel }: OverlayCardProps) {
  const interactive = Boolean(onActivate);
  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? ariaLabel : undefined}
      onClick={onActivate}
      onKeyDown={
        interactive
          ? (e) => { if (e.key === 'Enter' && e.target === e.currentTarget) onActivate!(); }
          : undefined
      }
      className={`${CARD_BASE} ${CARD_VARIANT[variant]} ${toneCls} ${interactive ? CARD_INTERACTIVE : ''}`}
    >
      {children}
    </div>
  );
}

/* ------------------------------- alerts -------------------------------- */

export function AlertCardBody({
  alert,
  nowMs,
  isOwner,
  onOpen,
  onDismiss,
}: {
  alert: CrmAlert;
  nowMs: number;
  isOwner: boolean;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  const def = defFor(alert);
  const Icon = def.icon;
  const remainingMs = Math.max(0, new Date(alert.escalate_at).getTime() - nowMs);
  const mm = Math.floor(remainingMs / 60000);
  const ss = Math.floor((remainingMs % 60000) / 1000);
  const badge = def.badge(alert);
  const detail = def.detail(alert);

  return (
    <OverlayCard
      variant="alert"
      toneCls={def.ring}
      onActivate={onOpen}
      ariaLabel={`${def.title} — ${alert.first_name || 'New lead'}`}
    >
      <div className={`flex items-center gap-2 px-4 py-2 text-white ${def.band}`}>
        <Icon className="h-4 w-4 shrink-0" />
        <span className="text-sm font-semibold">{def.title}</span>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {alert.first_name || 'New lead'}
          </span>
          {badge && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}>{badge.label}</span>
          )}
        </div>
        {detail && (
          <p className="mt-0.5 truncate text-sm text-gray-600 dark:text-gray-400">{detail}</p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {alert.status === 'assigned'
            ? isOwner
              ? def.showsCountdown
                ? `Assigned to you — team alerted in ${mm}:${pad(ss)}`
                : 'Assigned to you'
              : `Assigned to ${alert.assigned_user_name || 'a recruiter'}`
            : def.escalatedLine
              ? def.escalatedLine({ mmss: `${mm}:${pad(ss)}`, expired: remainingMs <= 0 })
              : def.showsCountdown
                ? 'Escalated — call it if you can'
                : `Waiting since ${waitingFor(alert.created_at, nowMs)}`}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 sm:min-h-0"
          >
            <Icon className="h-4 w-4" /> {def.action}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
          >
            Ignore
          </button>
        </div>
      </div>
    </OverlayCard>
  );
}

/* ------------------------------ callbacks ------------------------------ */

/**
 * The booked-call reminder.
 *
 * The feed is widened by out-of-office coverage, so this card can be about a
 * patient the viewer has never spoken to — held only because its assignee is
 * away today. When the row carries `coverage` the badge says whose lead it is;
 * on the viewer's own calls it renders nothing, so the card is unchanged from
 * the layout recruiters already have muscle memory for.
 */
export function CallbackCardBody({ card, onOpen, onDismiss }: { card: CallbackCard; onOpen: () => void; onDismiss: () => void }) {
  return (
    <OverlayCard onActivate={onOpen} ariaLabel={`Open ${card.firstName || 'the patient with an upcoming call'}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2">
            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Upcoming call</p>
              <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">
                {card.firstName || 'A patient'} — {timeLabel(card.scheduledAt)}
                {card.window === 'm1' ? ' (in ~1 min)' : ' (in ~10 min)'}
              </p>
              {card.coverage && (
                <div className="mt-1">
                  <CoverageBadge coverage={card.coverage} compact />
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            aria-label="Dismiss reminder"
            className={CLOSE_BTN}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </OverlayCard>
  );
}

/* --------------------------------- sms --------------------------------- */

export function SmsCardBody({ card, onOpen, onDismiss }: { card: SmsCard; onOpen: () => void; onDismiss: () => void }) {
  return (
    <OverlayCard onActivate={onOpen} ariaLabel={`Open ${card.name || 'the lead who just texted'}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2">
            <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {card.name || 'New text message'}
              </p>
              {/* A reply from someone on an outreach list — the text the list
                  exists to get, so it says which list. */}
              {card.listName && (
                <p className="mt-0.5 truncate text-xs font-medium text-primary-700 dark:text-primary-300">
                  Reply on {card.listName}
                </p>
              )}
              {card.preview && (
                <p className="mt-0.5 line-clamp-2 text-sm text-gray-700 dark:text-gray-300">
                  {card.preview}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            aria-label="Dismiss message"
            className={CLOSE_BTN}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </OverlayCard>
  );
}

/* -------------------------------- rating -------------------------------- */

/**
 * The hang-up rating prompt. Star taps save immediately (optimistic "Saved ✓")
 * and the optional note PATCHes the same row — v1's flow exactly. `onInteract`
 * hands the card its full auto-dismiss budget back, which is how v1's "restart
 * the 30s timer when a star lands" survives the move onto the queue's clock.
 *
 * A `test-` card never touches the API: the stars and the note form work, and
 * nothing is written.
 */
export function RatingCardBody({
  call,
  isTest,
  onDismiss,
  onInteract,
}: {
  call: LastCall;
  isTest: boolean;
  onDismiss: () => void;
  onInteract: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [savedRating, setSavedRating] = useState(0);
  const [note, setNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset per prompt appearance — a new call replaces the prompt wholesale.
  useEffect(() => {
    setSaving(false);
    setSavedId(null);
    setSavedRating(0);
    setNote('');
    setNoteSaving(false);
    setError(null);
  }, [call]);

  const rate = async (stars: number) => {
    if (saving || savedId) return;
    onInteract();
    setError(null);
    setSavedRating(stars); // optimistic
    if (isTest) { setSavedId('test'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/api/crm/contacts/${call.contactId}/ratings`, {
        rating: stars,
        ...(call.callSid ? { call_sid: call.callSid } : {}),
      });
      setSavedId(res.data?.rating?.id || null);
    } catch (e: any) {
      setSavedRating(0);
      setError(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Could not save the rating');
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async (e: FormEvent) => {
    e.preventDefault();
    if (!savedId || noteSaving) return;
    if (!note.trim() || isTest) { onDismiss(); return; }
    setNoteSaving(true);
    try {
      await api.patch(`/api/crm/ratings/${savedId}`, { note: note.trim() });
      onDismiss();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Could not save the note');
      setNoteSaving(false);
    }
  };

  return (
    <OverlayCard>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            How did the call with {call.name || 'the patient'} go?
          </p>
          <button type="button" onClick={onDismiss} aria-label="Skip rating" className={CLOSE_BTN}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-2 flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => rate(n)}
              disabled={saving || !!savedId}
              aria-label={`Rate ${n} of 5`}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition-colors duration-150 hover:bg-amber-50 disabled:cursor-default dark:hover:bg-amber-900/20"
            >
              <Star
                className={`h-6 w-6 ${n <= savedRating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}
              />
            </button>
          ))}
          {saving && <Loader2 className="ml-1 h-4 w-4 animate-spin text-gray-400" />}
          {savedId && (
            <span className="ml-1 inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
              <Check className="h-3.5 w-3.5" /> Saved
            </span>
          )}
        </div>

        {error && <p role="alert" className="mt-1.5 text-xs text-red-600 dark:text-red-400">{error}</p>}

        {savedId ? (
          <form onSubmit={saveNote} className="mt-2 flex items-center gap-2">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              placeholder="How was the interaction? (no medical details)"
              aria-label="Rating note"
              className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:min-h-0"
            />
            <button
              type="submit"
              disabled={noteSaving}
              className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            >
              {noteSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Done'}
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={onDismiss}
            className="mt-2 min-h-[44px] text-xs font-medium text-gray-400 transition-colors duration-150 hover:text-gray-600 dark:hover:text-gray-300 sm:min-h-0"
          >
            Skip
          </button>
        )}
      </div>
    </OverlayCard>
  );
}

/* ---------------------------- review finished ---------------------------- */

/**
 * "The AI review you asked for has finished." Deliberately a quiet toast and
 * not an alert: nothing is waiting on the phone, and the same review also sits
 * on Today's "AI reviews ready" lane until it is signed off. The verdict chip
 * carries the news, so a recruiter can triage without opening anything.
 */
export function ReviewReadyCardBody({ card, onOpen, onDismiss }: {
  card: ReviewReadyCard; onOpen: () => void; onDismiss: () => void;
}) {
  const failed = card.status === 'failed';
  const label = resultLabel(card);
  const tone = failed
    ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
    : card.overallResult === 'eligible'
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      : card.overallResult === 'excluded'
        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
        : card.overallResult === 'review_required'
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  return (
    <OverlayCard
      onActivate={onOpen}
      ariaLabel={failed
        ? `Open ${card.contactName || 'the lead'} — AI review failed`
        : `Open the AI review results for ${card.contactName || 'this lead'}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2">
            <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {failed ? 'AI review failed' : 'AI review ready'}
              </p>
              <p className="mt-0.5 truncate text-sm text-gray-700 dark:text-gray-300">
                {card.contactName || 'A lead'}
                {card.studyTitle ? ` — ${card.studyTitle}` : ''}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
                  {label}
                </span>
                {card.pendingCount > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {card.pendingCount} to verify
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            aria-label="Dismiss review notification"
            className={CLOSE_BTN}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </OverlayCard>
  );
}

/* --------------------------- outreach assigned --------------------------- */

/**
 * "Luke gave you 106 people for the Diabetic Neuropathy Study." Shown once per
 * list and never self-dismissing (see overlayQueue), so it carries its own
 * primary action: the recruiter can start working straight from the corner.
 */
export function OutreachAssignedCardBody({ card, onOpen, onDismiss }: {
  card: OutreachAssignedCard; onOpen: () => void; onDismiss: () => void;
}) {
  return (
    <OverlayCard onActivate={onOpen} ariaLabel={`Work through ${card.listName}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2">
            <ListChecks className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">New outreach list</p>
              <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">{card.line}</p>
              <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{card.listName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            aria-label="Dismiss outreach list notice"
            className={CLOSE_BTN}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 sm:min-h-0"
        >
          <ListChecks className="h-4 w-4" /> Work through
        </button>
      </div>
    </OverlayCard>
  );
}

/* -------------------------------- mention -------------------------------- */

/**
 * "Luke mentioned you on Jane Smith" (or "Dana replied to your note on Jane
 * Smith"), the note's first words, and Open
 * (docs/plans/crm-report-builder-search-collab-plan.md B3.4). A quiet toast,
 * not an alert: a teammate is waiting on you, not a patient on the phone, and
 * the mention also sits on Today's Mentions lane until it is read. Open goes to
 * the lead, scrolled to that note, and marks the mention read.
 */
export function MentionCardBody({ card, onOpen, onDismiss }: {
  card: MentionCard; onOpen: () => void; onDismiss: () => void;
}) {
  const Icon = card.kind === 'reply' ? Reply : AtSign;
  return (
    <OverlayCard onActivate={onOpen} ariaLabel={`Open the note: ${card.line}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2">
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{card.line}</p>
              {card.snippet && (
                <p className="mt-0.5 line-clamp-2 break-words text-sm text-gray-700 dark:text-gray-300">
                  {card.snippet}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            aria-label="Dismiss mention"
            className={CLOSE_BTN}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800 sm:min-h-0"
        >
          Open
        </button>
      </div>
    </OverlayCard>
  );
}

/* ------------------------------- overflow ------------------------------- */

/** The "+N more" pill. Collapsed it counts the waiting room; expanded it closes it. */
export function OverflowPill({
  count,
  expanded,
  onToggle,
}: {
  count: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="pointer-events-auto inline-flex min-h-[44px] items-center gap-1.5 self-end rounded-full border border-gray-200 bg-white px-3 text-xs font-medium text-gray-600 shadow-lg transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/60 sm:min-h-0 sm:py-1.5"
    >
      {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
      {expanded ? 'Show less' : `+${count} more`}
    </button>
  );
}
